import React, { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { UserPlus, Copy, CheckCircle2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import ServicePicker from "@/components/ServicePicker";
import { CertificationsCard } from "@/components/collector/ProfileMedia";

const blank = { name: "", phone: "", email: "", zip_code: "", services: [] };

const CertStep = ({ collectorId }) => {
  const [profile, setProfile] = useState({ certifications: [] });
  const base = `/company/collectors/${collectorId}`;
  const reload = async () => { const { data } = await api.get(base); setProfile(data); };
  return (
    <div data-testid="add-collector-cert-step">
      <div className="text-sm font-medium text-[#1E3F33]">Add their certifications <span className="text-[#8A9A86] font-normal">(optional)</span></div>
      <p className="text-xs text-[#8A9A86] mb-2">Upload now or later from Manage. They need at least one certification before they receive leads.</p>
      <CertificationsCard profile={profile} base={base} reload={reload} />
    </div>
  );
};

export const CopyLink = ({ link, testid = "invite-link" }) => (
  <div className="flex items-center gap-2" data-testid={`${testid}-row`}>
    <input readOnly className="input-soft text-xs flex-1" value={link} data-testid={testid} />
    <button type="button" onClick={() => { navigator.clipboard?.writeText(link); toast.success("Invite link copied"); }} className="btn-secondary text-xs px-3 inline-flex items-center gap-1" data-testid={`${testid}-copy`}>
      <Copy className="w-3.5 h-3.5"/> Copy
    </button>
  </div>
);

export const AddCollectorDialog = ({ open, onOpenChange, onAdded }) => {
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const close = (o) => { if (!o) { setForm(blank); setResult(null); } onOpenChange(o); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.phone.trim() && !form.email.trim()) return toast.error("Enter a phone number or email so we can send the invite.");
    setBusy(true);
    try {
      const { data } = await api.post("/company/collectors", { name: form.name, phone: form.phone || null, email: form.email || null, zip_code: form.zip_code || null, services: form.services });
      setResult(data);
      toast.success(data.message);
      await onAdded();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="add-collector-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-[#1E3F33]">Add a collector</DialogTitle>
          <DialogDescription>We'll text and/or email them an invite to create their own login. You can fill in their profile yourself right away, or let them complete it.</DialogDescription>
        </DialogHeader>
        {result ? (
          <div className="space-y-4" data-testid="add-collector-result">
            <div className="flex items-start gap-2 text-sm text-[#2D6A4F]"><CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0"/> {result.message}</div>
            {result.invite_link && <div><label className="label-soft">Invite link (share it directly if needed)</label><CopyLink link={result.invite_link} /></div>}
            {result.kind === "new" && result.collector_id && <CertStep collectorId={result.collector_id} />}
            <div className="flex gap-2">
              <button type="button" onClick={() => { setForm(blank); setResult(null); }} className="btn-secondary text-sm" data-testid="add-another-collector">Add another</button>
              <button type="button" onClick={() => close(false)} className="btn-primary text-sm" data-testid="add-collector-done">Done</button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div><label className="label-soft">Collector name *</label><input required data-testid="add-collector-name" className="input-soft" value={form.name} onChange={set("name")} placeholder="First and last name"/></div>
            <div><label className="label-soft">Mobile phone</label><input data-testid="add-collector-phone" type="tel" className="input-soft" value={form.phone} onChange={set("phone")} placeholder="(555) 123-4567"/></div>
            <div><label className="label-soft">Email</label><input data-testid="add-collector-email" type="email" className="input-soft" value={form.email} onChange={set("email")} placeholder="name@example.com"/></div>
            <div><label className="label-soft">Home ZIP code</label><input data-testid="add-collector-zip" inputMode="numeric" maxLength={5} className="input-soft" value={form.zip_code} onChange={set("zip_code")} placeholder="e.g. 78704"/></div>
            <div data-testid="add-collector-services">
              <label className="label-soft">Services this collector offers</label>
              <p className="text-xs text-[#8A9A86] mb-2">Customers are matched by ZIP code and service, so pick everything they can do.</p>
              <ServicePicker selected={form.services} showSupplies={false} onChange={(svcs) => setForm({ ...form, services: svcs })} />
            </div>
            <p className="text-xs text-[#8A9A86]">Phone or email is required to send the invite. If they already have a collector account, they'll get a request to join your company instead (they keep their own services). Each collector adds $10.99/month to your subscription. You can add certifications on the next step.</p>
            <button disabled={busy} data-testid="add-collector-submit" className="btn-primary w-full inline-flex items-center justify-center gap-2"><UserPlus className="w-4 h-4"/> {busy ? "Sending invite…" : "Add & send invite"}</button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
